import { GlobalState, UI } from '@leverege/ui-redux';
import React from 'react';
import I18N from '@leverege/i18n';
import { Config } from '@leverege/plugin';
import { System as ApiSystem } from '@leverege/api-redux';
import { UserRoles } from '@leverege/molten-core';
import UserManagementScreen from "../screens/userManagement/LazyUserManagementScreen.js";
import ApiAccessKeyManagementScreen from "../screens/apiAccessKeyManagement/LazyApiAccessKeyManagementScreen.js";
import { LinkAction } from "../routes/index.js";
const {
  t: sysT,
  tf: sysTf,
  tfIcon: sysTfIcon
} = I18N.ns('molten.imagine.SystemUserAccess');
function createSystemUserAccess(molten, system, options) {
  const {
    sort,
    sectionName
  } = options;

  // Check if system has user management.
  if (!system.hasTag('has:userManagement')) {
    return;
  }
  const path = `${options.rootUrl}/access`;
  const excludeFrom = system.getTagsStartingWith('excludeUserManagementFrom:', true);
  const allUserRoles = UI.get(GlobalState.get(), 'allUserRoles');
  const resourceId = system.id;

  // Create the role filter
  const roleFilter = UserRoles.createSystemRoleFilter(allUserRoles, [resourceId], molten);
  molten.addPlugin('Action', LinkAction.create({
    id: 'link.SystemUsers',
    matches: {
      client: 'Main',
      use: 'navBar'
    },
    name: sysTf('name'),
    icon: sysTfIcon('icon'),
    path,
    layout: {
      sort,
      sectionName
    },
    handles: cxt => {
      const aPersona = UI.get(GlobalState.get()).activePersona?.name;
      if (shouldExclude(aPersona, null, excludeFrom, [])) {
        return false;
      }
      return true;
    }
  }));

  // Add route
  molten.addPlugin('Route', {
    // id : `${blueprint.type}.route.ResourceUsers`,
    id: 'route.SystemUsers',
    exact: true,
    path,
    matches: {
      client: 'Main'
    },
    render: ({
      match,
      location,
      history,
      ...rest
    }) => {
      const props = {
        ...rest,
        match,
        location,
        history,
        title: sysT('title'),
        selectorProps: {
          simple: true,
          roleFilter
        },
        variants: {
          editorPane: 'formBody',
          inputLabel: 'inputTitle',
          inputText: 'default',
          inputCaption: 'caption',
          selectionSubtitle: 'caption',
          titleBar: 'screenTitle',
          toolbar: 'actionBar'
        },
        projectId: molten.getConfig('api/projectId'),
        resourceId,
        systemId: resourceId,
        actions: ApiSystem(resourceId),
        emailOptions: molten.getConfig('userManagement/emailOptions'),
        smsOptions: molten.getConfig('userManagement/smsOptions'),
        allowSmsInvitation: molten.getConfig('userManagement/allowSmsInvitation'),
        uiName: molten.getConfig('uiName')
      };
      return /*#__PURE__*/React.createElement(UserManagementScreen, props);
    }
  });
}
function createSystemApiAccess(molten, system, options) {
  const {
    sort,
    sectionName
  } = options;

  // Check if system has api access keys management.
  if (!system.hasTag('has:apiAccessKeyManagement')) {
    return;
  }
  const path = `${options.rootUrl}/apiAccess`;
  const excludeFrom = system.getTagsStartingWith('excludeApiAccessKeyManagementFrom:', true);
  const allUserRoles = UI.get(GlobalState.get(), 'allUserRoles');
  const resourceId = system.id;
  const roleFilter = UserRoles.createSystemRoleFilter(allUserRoles, [resourceId], molten);
  molten.addPlugin('Action', LinkAction.create({
    id: 'link.SystemApiAccessKeys',
    matches: {
      client: 'Main',
      use: 'navBar'
    },
    name: Config.get('SystemApiAccessKeys', 'navActionName', Config.get('ApiAccessKeys', 'navActionName', 'Api Access Keys')),
    icon: Config.get('SystemApiAccessKeys', 'navIcon', Config.get('ApiAccessKeys', 'navIcon', 'https://molten-ui-assets.storage.googleapis.com/access-key-nav.svg')),
    path,
    layout: {
      sort,
      sectionName
    },
    handles: cxt => {
      const aPersona = UI.get(GlobalState.get()).activePersona?.name;
      if (shouldExclude(aPersona, null, excludeFrom, [])) {
        return false;
      }
      return true;
    }
  }));

  // Add route
  molten.addPlugin('Route', {
    id: 'route.SystemApiAccessKeys',
    exact: true,
    path,
    matches: {
      client: 'Main'
    },
    render: ({
      match,
      location,
      history,
      ...rest
    }) => {
      const delegate = ApiSystem(resourceId);
      const props = {
        ...rest,
        match,
        location,
        history,
        title: Config.get('SystemApiAccessKeys', 'navActionName', Config.get('ApiAccessKeys', 'navActionName', 'Api Access Keys')),
        selectorProps: {
          simple: true,
          roleFilter
        },
        variants: {
          editorPane: 'formBody',
          inputLabel: 'inputTitle',
          inputText: 'default',
          inputCaption: 'caption',
          selectionSubtitle: 'caption',
          copyToClipboardSmall: 'copyToClipboard',
          copyToClipboardBig: 'copyToClipboardButton',
          titleBar: 'screenTitle',
          toolbar: 'actionBar'
        },
        projectId: molten.getConfig('api/projectId'),
        resourceId,
        delegate,
        actions: delegate
      };
      return /*#__PURE__*/React.createElement(ApiAccessKeyManagementScreen, props);
    }
  });
}
function shouldExclude(persona, attribute, excludeFrom, attrExcludeFrom) {
  return attribute == null && (excludeFrom.includes(persona) || excludeFrom.includes('*')) || attrExcludeFrom.includes(persona) || attrExcludeFrom.includes('*');
}
export default {
  createSystemUserAccess,
  createSystemApiAccess,
  isSortable(attr) {
    const nonSortable = attr.hasTag('sortable:false');
    if (nonSortable) {
      return false;
    }
    if (attr.type === 'geoPoint' || attr.type === 'geoJson' || attr.type === 'relationship' || attr.type === 'parentRelationship') {
      return false;
    }
    return true;
  },
  isFilterable(attr) {
    const nonFilterable = attr.hasTag('filterable:false');
    if (nonFilterable) {
      return false;
    }
    if (attr.type === 'relationship') {
      return false;
    }
    return true;
  },
  shouldExclude
};